import React, { useEffect, useRef, useState } from 'react';
import type { AxiosError } from 'axios';
import { XMarkIcon } from '@heroicons/react/24/outline';
import {
  useSubscribe,
  useConfirmSubscription,
  useChangePlan,
  useChangeSeats,
} from '../../../services/billing';
import { useRevolut, type RevolutFieldController } from '../../../contexts/RevolutContext';
import type { PlanCatalogItem, PlanId, SubscriptionDetails, UsageData } from '../../../types/billing';

/** What the modal is being opened to do. */
export type SubscribeIntent =
  // `seats` pre-fills the form when resuming an unfinished checkout.
  | { kind: 'subscribe'; planId: PlanId; seats?: number }
  | { kind: 'change-plan'; planId: PlanId }
  | { kind: 'change-seats' };

interface SubscribeModalProps {
  isOpen: boolean;
  onClose: () => void;
  intent: SubscribeIntent;
  /** Current subscription (drives change flows + seat floor). */
  subscription: SubscriptionDetails | null;
  /** Live usage — seats can't drop below the members already in use. */
  usage: UsageData | null;
  /** Plan catalog for pricing + trial copy. */
  catalog: PlanCatalogItem[];
  onSuccess?: () => void;
}

/** Backend error code → user-facing copy. */
const ERROR_MESSAGES: Record<string, string> = {
  NOT_SELF_SERVE: 'This plan is not available for self-service checkout. Please contact sales.',
  SEATS_BELOW_MEMBERS: 'You have more members than seats selected. Increase the seat count.',
  ALREADY_SUBSCRIBED: 'This workspace already has an active subscription.',
  CARD_ERROR: 'Your card was declined. Please try a different card.',
  REVOLUT_ERROR: 'The payment provider is unavailable right now. Please try again shortly.',
  VALIDATION_ERROR: 'Some of the details are invalid. Please review and try again.',
};

const mapError = (err: unknown, fallback: string): string => {
  const code = (err as AxiosError<{ error?: string }>)?.response?.data?.error;
  if (code && ERROR_MESSAGES[code]) return ERROR_MESSAGES[code];
  return fallback;
};

const formatCurrency = (amountMinor: number, currency: string): string => {
  try {
    return new Intl.NumberFormat('en-IE', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amountMinor / 100);
  } catch {
    return `${(amountMinor / 100).toFixed(2)} ${currency.toUpperCase()}`;
  }
};

const titleCase = (value: string): string =>
  value.length === 0 ? value : value.charAt(0).toUpperCase() + value.slice(1);

const Spinner: React.FC = () => (
  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24" aria-hidden="true">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

/**
 * Subscribe / change-plan / change-seats flow.
 *
 * For a brand-new subscription we call POST /billing/subscribe, mount the
 * Revolut embedded card field with the returned token (via `useRevolut`), and
 * on the widget's success confirm with POST /billing/subscription/confirm.
 * Plan and seat changes on an existing subscription reuse the saved payment
 * method server-side, so they skip the card field entirely.
 */
export default function SubscribeModal({
  isOpen,
  onClose,
  intent,
  subscription,
  usage,
  catalog,
  onSuccess,
}: SubscribeModalProps) {
  const subscribeMutation = useSubscribe();
  const confirmMutation = useConfirmSubscription();
  const changePlanMutation = useChangePlan();
  const changeSeatsMutation = useChangeSeats();
  const { mountCardField } = useRevolut();

  const targetPlanId: PlanId =
    intent.kind === 'change-seats' ? subscription?.plan_id ?? 'free' : intent.planId;
  const plan = catalog.find((p) => p.id === targetPlanId);
  const planName = plan?.name ?? titleCase(targetPlanId);
  const needsPayment = intent.kind === 'subscribe';

  const seatFloor = Math.max(1, usage?.seats_used ?? 1);

  const [seats, setSeats] = useState<number>(seatFloor);
  const [billingEmail, setBillingEmail] = useState('');
  const [cardholderName, setCardholderName] = useState('');
  const [phase, setPhase] = useState<'form' | 'card'>('form');
  const [pendingToken, setPendingToken] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [cardReady, setCardReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cardTargetRef = useRef<HTMLDivElement>(null);
  const controllerRef = useRef<RevolutFieldController | null>(null);
  const subscribedIdRef = useRef<string | null>(null);

  // Keep a stable success handler for the card widget that always sees the
  // latest state/props (the mount effect captures it once).
  const onCardSuccessRef = useRef<() => void>(() => {});
  onCardSuccessRef.current = () => {
    const subId = subscribedIdRef.current;
    if (!subId) return;
    const stillProcessing = () => {
      setProcessing(false);
      setError('Payment succeeded but confirmation is still processing. It will finalize shortly.');
    };
    confirmMutation
      .mutateAsync({ subscription_id: subId })
      .then((result) => {
        // A 200 is not activation: Revolut's subscription state can lag the
        // card charge. Closing on 'pending' would drop a customer who just paid
        // onto "payment hasn't come through" — the billing read heals it later.
        if (result.status !== 'active' && result.status !== 'trialing') {
          stillProcessing();
          return;
        }
        onSuccess?.();
        closeAndReset();
      })
      .catch(stillProcessing);
  };

  const closeAndReset = () => {
    controllerRef.current?.destroy();
    controllerRef.current = null;
    subscribedIdRef.current = null;
    setPhase('form');
    setPendingToken(null);
    setProcessing(false);
    setCardReady(false);
    setError(null);
    onClose();
  };

  // Re-seed the seat/email fields each time the modal opens.
  useEffect(() => {
    if (!isOpen) return;
    const included = plan?.included_seats ?? 0;
    const initialSeats =
      intent.kind === 'change-seats'
        ? subscription?.seats ?? seatFloor
        : intent.kind === 'subscribe' && intent.seats
          ? Math.max(seatFloor, intent.seats)
          : Math.max(seatFloor, included, subscription?.seats ?? 0, 1);
    setSeats(initialSeats);
    setBillingEmail('');
    setCardholderName('');
    setPhase('form');
    setPendingToken(null);
    setProcessing(false);
    setCardReady(false);
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Mount the Revolut card field once we enter the card phase with a token.
  useEffect(() => {
    if (phase !== 'card' || !pendingToken) return;
    const target = cardTargetRef.current;
    if (!target) return;

    let cancelled = false;
    setCardReady(false);
    mountCardField({
      token: pendingToken,
      target,
      onSuccess: () => onCardSuccessRef.current(),
      onError: (message) => {
        setProcessing(false);
        setError(message);
      },
    })
      .then((controller) => {
        if (cancelled) {
          controller.destroy();
          return;
        }
        controllerRef.current = controller;
        setCardReady(true);
      })
      .catch(() => {
        if (!cancelled) setError('Could not load the secure card form. Please try again.');
      });

    return () => {
      cancelled = true;
      controllerRef.current?.destroy();
      controllerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, pendingToken]);

  if (!isOpen) return null;

  const seatsInvalid = seats < seatFloor || !Number.isFinite(seats);
  const monthlyEstimate = plan
    ? formatCurrency(plan.base_cents + plan.per_seat_cents * seats, plan.currency)
    : null;

  const title =
    intent.kind === 'subscribe'
      ? `Subscribe to ${planName}`
      : intent.kind === 'change-plan'
        ? `Switch to ${planName}`
        : 'Change seats';

  const submitLabel =
    intent.kind === 'subscribe'
      ? 'Continue to payment'
      : intent.kind === 'change-plan'
        ? 'Confirm plan change'
        : 'Update seats';

  const handleSubmitForm = async () => {
    if (seatsInvalid) return;
    setError(null);
    setProcessing(true);
    try {
      if (intent.kind === 'subscribe') {
        const result = await subscribeMutation.mutateAsync({
          plan_id: targetPlanId,
          seats,
          billing_email: billingEmail.trim() || undefined,
        });
        subscribedIdRef.current = result.subscription_id;
        setPendingToken(result.revolut_token);
        setPhase('card');
        setProcessing(false); // the card phase manages its own processing state
      } else if (intent.kind === 'change-plan') {
        if (targetPlanId !== subscription?.plan_id) {
          await changePlanMutation.mutateAsync({ plan_id: targetPlanId });
        }
        if (seats !== (subscription?.seats ?? seats)) {
          await changeSeatsMutation.mutateAsync({ seats });
        }
        onSuccess?.();
        closeAndReset();
      } else {
        await changeSeatsMutation.mutateAsync({ seats });
        onSuccess?.();
        closeAndReset();
      }
    } catch (err) {
      setProcessing(false);
      setError(mapError(err, 'Something went wrong. Please try again.'));
    }
  };

  const handlePay = () => {
    if (!controllerRef.current) return;
    setError(null);
    setProcessing(true);
    controllerRef.current.submit({
      name: cardholderName.trim() || undefined,
      email: billingEmail.trim() || undefined,
    });
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={closeAndReset} />

      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="relative bg-surface-primary rounded-lg shadow-xl max-w-lg w-full p-6">
          <button
            onClick={closeAndReset}
            className="absolute top-4 right-4 text-text-tertiary hover:text-text-primary"
            aria-label="Close"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>

          <h2 className="text-xl font-bold text-text-primary mb-1">{title}</h2>
          {plan && (
            <p className="text-sm text-text-secondary mb-5">
              {formatCurrency(plan.base_cents, plan.currency)} base +{' '}
              {formatCurrency(plan.per_seat_cents, plan.currency)} per seat / month
              {plan.trial_days > 0 && intent.kind === 'subscribe'
                ? ` · ${plan.trial_days}-day free trial`
                : ''}
            </p>
          )}

          {phase === 'form' ? (
            <div className="space-y-5">
              {/* Seats */}
              <div>
                <label
                  htmlFor="seat-count"
                  className="block text-sm font-medium text-text-primary mb-2"
                >
                  Seats
                </label>
                <input
                  id="seat-count"
                  type="number"
                  min={seatFloor}
                  value={seats}
                  onChange={(e) => setSeats(Number(e.target.value))}
                  className="w-full px-4 py-2 border border-border-primary rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-surface-primary text-text-primary"
                />
                <p className="mt-1 text-xs text-text-tertiary">
                  A seat is a seat — trainer or learner. You currently use{' '}
                  {usage?.seats_used ?? 0} seat{usage?.seats_used === 1 ? '' : 's'}, so seats can't
                  go below {seatFloor}.
                </p>
                {seatsInvalid && (
                  <p className="mt-1 text-xs text-error-600 dark:text-error-400">
                    Seats must be at least {seatFloor}.
                  </p>
                )}
              </div>

              {/* Billing email (subscribe only) */}
              {needsPayment && (
                <div>
                  <label
                    htmlFor="billing-email"
                    className="block text-sm font-medium text-text-primary mb-2"
                  >
                    Billing email (optional)
                  </label>
                  <input
                    id="billing-email"
                    type="email"
                    value={billingEmail}
                    onChange={(e) => setBillingEmail(e.target.value)}
                    placeholder="billing@example.com"
                    className="w-full px-4 py-2 border border-border-primary rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-surface-primary text-text-primary"
                  />
                  <p className="mt-1 text-xs text-text-tertiary">
                    Leave blank to use your account email.
                  </p>
                </div>
              )}

              {/* Estimate */}
              {monthlyEstimate && (
                <div className="bg-primary-50 dark:bg-primary-900/20 rounded-lg p-4 border border-primary-200 dark:border-primary-800">
                  <p className="text-sm text-text-secondary">Estimated monthly total</p>
                  <p className="text-2xl font-bold text-text-primary">{monthlyEstimate}</p>
                  <p className="mt-1 text-xs text-text-tertiary">
                    Final amount is calculated by Revolut at checkout.
                  </p>
                </div>
              )}

              {error && (
                <div className="bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800 rounded-lg p-4">
                  <p className="text-sm text-error-800 dark:text-error-200">{error}</p>
                </div>
              )}

              <div className="flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={closeAndReset}
                  disabled={processing}
                  className="px-6 py-2 border border-border-primary rounded-full font-medium text-text-primary hover:bg-grey-50 dark:hover:bg-grey-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSubmitForm}
                  disabled={processing || seatsInvalid}
                  className="px-6 py-2 bg-primary-500 dark:bg-primary-350 rounded-full font-medium hover:bg-primary-600 dark:hover:bg-primary-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors inline-flex items-center gap-2"
                >
                  {processing && <Spinner />}
                  {submitLabel}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-5">
              {/* Cardholder name */}
              <div>
                <label
                  htmlFor="cardholder-name"
                  className="block text-sm font-medium text-text-primary mb-2"
                >
                  Cardholder name
                </label>
                <input
                  id="cardholder-name"
                  type="text"
                  value={cardholderName}
                  onChange={(e) => setCardholderName(e.target.value)}
                  placeholder="Jane Doe"
                  className="w-full px-4 py-2 border border-border-primary rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-surface-primary text-text-primary"
                />
              </div>

              {/* Revolut card field mounts here */}
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Card details
                </label>
                <div
                  ref={cardTargetRef}
                  className="p-4 min-h-[52px] border border-border-primary rounded-lg bg-surface-primary"
                />
                <p className="mt-2 text-xs text-text-tertiary">
                  Secured by Revolut. Your card details are entered directly into Revolut and never
                  touch our servers.
                </p>
              </div>

              {error && (
                <div className="bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800 rounded-lg p-4">
                  <p className="text-sm text-error-800 dark:text-error-200">{error}</p>
                </div>
              )}

              <div className="flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={closeAndReset}
                  disabled={processing}
                  className="px-6 py-2 border border-border-primary rounded-full font-medium text-text-primary hover:bg-grey-50 dark:hover:bg-grey-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handlePay}
                  disabled={processing || !cardReady}
                  className="px-6 py-2 bg-primary-500 dark:bg-primary-350 rounded-full font-medium hover:bg-primary-600 dark:hover:bg-primary-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors inline-flex items-center gap-2"
                >
                  {processing && <Spinner />}
                  {processing ? 'Processing…' : monthlyEstimate ? `Pay ${monthlyEstimate}` : 'Pay'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
